// assets/AllCourses/Exercises/DragAndDropExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function DragAndDropExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe questions
  const questions = Array.isArray(exercise.dragAndDropQuestions)
    ? exercise.dragAndDropQuestions
    : [];

  // Student state
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });
  const completedRef = useRef(false);

  // Initialize student state
  useEffect(() => {
    if (isPreview) {
      const initial = {
        questions: questions.map((q) => {
          const blankCount = (q.sentence.match(/___/g) || []).length;
          return {
            availableOptions: [...(q.options || [])].sort(() => Math.random() - 0.5),
            droppedAnswers: Array(blankCount).fill(""),
          };
        }),
        hasInteracted: false,
      };

      // Sync with saved attempt
      if (exercise.attempt && Array.isArray(exercise.attempt.questions)) {
        setState(exercise.attempt);
      } else {
        setState(initial);
      }
    }
  }, [isPreview, questions, exercise.attempt]);

  // Reset on exercise change
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("dragAndDropQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      options: [],
      answer: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blankCount = (value.match(/___/g) || []).length;
      const oldAnswer = Array.isArray(current.answer) ? current.answer : [];
      updated.answer = Array.from({ length: blankCount }, (_, i) => oldAnswer[i] || "");
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    const options = [...q.options];
    options[optIdx] = value;
    next[qIdx] = { ...q, options };
    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    next[qIdx] = { ...q, options: [...q.options, ""] };
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { options: [] };
    next[qIdx] = { ...q, options: q.options.filter((_, i) => i !== optIdx) };
    updateQuestions(next);
  };

  const handleAnswerChange = (qIdx, aIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { answer: [] };
    const answer = [...q.answer];
    answer[aIdx] = value;
    next[qIdx] = { ...q, answer };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        sentence: "",
        options: [],
        answer: [],
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleDrop = (qIdx, blankIdx, option) => {
    if (!isPreview || showResult || !option) return;

    setState((prev) => {
      const next = structuredClone(prev);
      const qState = next.questions[qIdx];
      if (!qState || !qState.availableOptions.includes(option)) return prev;

      qState.droppedAnswers[blankIdx] = option;
      qState.availableOptions = qState.availableOptions.filter((o) => o !== option);
      next.hasInteracted = true;

      setAttempt(next);

      const allFilled = next.questions.every((qs) =>
        qs.droppedAnswers.every(Boolean)
      );

      if (allFilled) {
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = (currentState) => {
    if (completedRef.current || !isPreview) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, qIdx) => {
      const userAnswers = currentState.questions[qIdx]?.droppedAnswers || [];
      return q.answer.every((ans, aIdx) => {
        const user = String(userAnswers[aIdx] || "").trim();
        const correct = String(ans || "").trim();

        if (q.requireCapitalization) {
          return user === correct;
        }
        return user.toLowerCase() === correct.toLowerCase();
      });
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions yet. Click "Add Question" to start.
          </p>
        )}

        {questions.map((q, qIdx) => {
          const blankCount = (q.sentence.match(/___/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Sentence ${qIdx + 1} (use ___ for each blank)`}
                value={q.sentence || ""}
                onChange={(v) => handleQuestionChange(qIdx, "sentence", v)}
                placeholder="The cat ___ on the ___ every day."
                rows={4}
                required
                error={errors?.[`exercise-${exercise.index}-sentence-${qIdx}`]}
              />

              {/* Options */}
              <div className="space-y-3">
                <label className="font-medium">Options</label>
                {q.options.map((opt, optIdx) => (
                  <div key={optIdx} className="flex gap-3 items-end">
                    <StringField
                      value={opt}
                      onChange={(v) => handleOptionChange(qIdx, optIdx, v)}
                      placeholder="Option"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveOption(qIdx, optIdx)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => handleAddOption(qIdx)}>
                  Add Option
                </Button>
              </div>

              {/* Answers */}
              <div className="space-y-3">
                <label className="font-medium">Correct Answers (one per blank)</label>
                {blankCount === 0 ? (
                  <p className="text-sm text-gray-500">
                    Add ___ in the sentence to create blanks
                  </p>
                ) : (
                  Array.from({ length: blankCount }).map((_, aIdx) => (
                    <StringField
                      key={aIdx}
                      label={`Blank ${aIdx + 1}`}
                      value={q.answer[aIdx] || ""}
                      onChange={(v) => handleAnswerChange(qIdx, aIdx, v)}
                      placeholder="Correct word"
                    />
                  ))
                )}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) => handleCapitalizationChange(qIdx, c)}
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveQuestion(qIdx)}
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No drag and drop questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>
          Drag the words from the bank to complete the sentences.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const sentenceParts = q.sentence.split("___");
          const qState = state.questions[qIdx] || { availableOptions: [], droppedAnswers: [] };

          return (
            <div key={qIdx} className="space-y-6">
              {/* Word Bank */}
              <div className="flex flex-wrap gap-3 p-4 bg-muted rounded-lg justify-center min-h-[80px]">
                {qState.availableOptions.length > 0 ? (
                  qState.availableOptions.map((opt) => (
                    <Button
                      key={opt}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", opt)}
                      variant="secondary"
                      className="cursor-grab active:cursor-grabbing"
                    >
                      {opt}
                    </Button>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">
                    All words used
                  </span>
                )}
              </div>

              {/* Sentence with Drop Zones */}
              <div className="text-lg leading-loose flex flex-wrap items-center gap-3 bg-background p-6 rounded-lg">
                {sentenceParts.map((part, i) => (
                  <React.Fragment key={i}>
                    <span>{part}</span>
                    {i < sentenceParts.length - 1 && (
                      <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const word = e.dataTransfer.getData("text/plain");
                          handleDrop(qIdx, i, word);
                        }}
                        className="inline-block"
                      >
                        <div
                          className={cn(
                            "inline-flex items-center justify-center min-w-32 h-10 border-2 border-dashed rounded-lg px-3",
                            qState.droppedAnswers[i]
                              ? "border-primary bg-primary/10"
                              : "border-gray-400"
                          )}
                        >
                          {qState.droppedAnswers[i] ? (
                            <span
                              className={cn(
                                "font-medium",
                                showResult &&
                                  (q.requireCapitalization
                                    ? qState.droppedAnswers[i] === q.answer[i]
                                    : qState.droppedAnswers[i].toLowerCase() === q.answer[i].toLowerCase())
                                  ? "text-green-700"
                                  : "text-red-700"
                              )}
                            >
                              {qState.droppedAnswers[i]}
                              {showResult && (
                                <span className="ml-2 text-sm">
                                  {q.requireCapitalization
                                    ? qState.droppedAnswers[i] === q.answer[i]
                                      ? "Correct"
                                      : `Wrong → ${q.answer[i]}`
                                    : qState.droppedAnswers[i].toLowerCase() === q.answer[i].toLowerCase()
                                    ? "Correct"
                                    : `Wrong → ${q.answer[i]}`}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-400">Drop here</span>
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}